/* eslint-disable react/no-children-prop */
/* eslint-disable no-console */
import React from "react";
import { Button, Rate, message } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  HeartFilled,
  HeartOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { ImageGallery } from "..";
import { useFirestoreCollection } from "../../../hooks/useFirestoreCollection";
import { fetchHotelImage94307FromFirestore } from "../../../firestore/firestoreService";
import { IHotel28355 } from "../../../common/types";
import {
  formatReadableAddress,
  formatReadableCity,
} from "../../../common/util/util";
import {
  hotelBirthdayPromoConfig,
  isHotelBirthdayPromoActive,
} from "../../../common/util/hotelBirthdayPromo";
import { HotelItemWrapper } from "./Hotel.style";
import { SuggestionBox } from "./SuggestionBox";
import {
  buildCtaLabel,
  buildSearchParams as buildSearchParamsHelper,
  buildGuestCounts,
  buildMinStayNotice,
  buildNightsLabel,
  buildOnRequestMessage,
  buildPriceLabels,
  buildRoomCountLine,
  buildSuggestions,
  calculateTotalForRange,
  formatPrice,
  getMealIncludeLine,
  shouldShowPriceColumn as shouldShowPriceColumnHelper,
  shouldShowSuggestions as shouldShowSuggestionsHelper,
} from "./hotelItemHelpers";

const partnerId = process.env.REACT_APP_SITE_ID || "";

interface IProps {
  hotelId: string;
  hotel: IHotel28355;
  isMobile: boolean;
  filter: any;
  guestParams?: {
    adultNumbers: number;
    childrenNumber: number;
    infant: number;
    rooms: number;
    childrenAges?: string | string[];
  };
  priceSummary?: {
    totalPrice: number;
    startDate?: string | null;
    endDate?: string | null;
    nights: number;
    rooms?: any[];
    periods?: any[];
    primaryRoom?: any;
    sellingPrice?: number;
    discountPrice?: number;
    minStay?: number;
    isMinStay?: boolean;
    onRequestType?: "full" | "short" | null;
    hasDirectAvailability?: boolean;
    guestsLabel?: string;
    availableRoomsCount?: number;
    hasEarlyBookingDiscount?: boolean;
  };
}
export const HotelItem: React.FC<IProps> = ({
  hotelId,
  hotel,
  isMobile,
  filter,
  guestParams,
  priceSummary,
}) => {
  const dispatch = useDispatch();
  const [isFavorite, setIsFavorite] = React.useState<boolean>(false);
  const [images, setImages] = React.useState([]);
  useFirestoreCollection({
    query: () => fetchHotelImage94307FromFirestore(hotelId),
    data: setImages,
    deps: [dispatch, hotelId],
    local: true,
    shouldExecute: !!hotelId,
  });

  // const handleViewHotel = (periodId: string) => () => {
  //   console.log(filter);

  //   if (isMobile) {
  //     history(
  //       `/hotel/${hotelId}?exclusiveOfferId=${periodId}&adultNumbers=${filter?.adultNumbers || 2}&childrenNumber=${filter?.childrenNumber || 0}&infant=${filter?.infant || 0}&roomNumber=1`,
  //     );
  //   } else {
  //     window.open(
  //       `/hotel/${hotelId}?exclusiveOfferId=${periodId}&adultNumbers=${filter?.adultNumbers || 2}&childrenNumber=${filter?.childrenNumber || 0}&infant=${filter?.infant || 0}&roomNumber=1`,
  //       "_blank",
  //     );
  //     // win.focus();
  //   }
  // };
  const navigate = useNavigate();
  const primaryRoom = priceSummary?.primaryRoom || (hotel as any)?.primaryRoom;
  const roomLine = primaryRoom?.name || "";
  const mealIncludeLine = getMealIncludeLine(primaryRoom);
  const { priceLabel, discountPriceLabel, hasDiscountPrice } = buildPriceLabels(
    priceSummary?.totalPrice,
    priceSummary?.discountPrice,
  );
  const nightsLabel = buildNightsLabel(priceSummary?.nights);
  const onRequestMessage = buildOnRequestMessage(priceSummary?.onRequestType);
  const normalizedPartnerId = partnerId
    ? partnerId.charAt(0).toUpperCase() + partnerId.slice(1).toLowerCase()
    : undefined;
  const showBirthdayPromo = isHotelBirthdayPromoActive({
    partnerId,
    hotelId,
  });
  const periodMap = React.useMemo(() => {
    const periods = priceSummary?.periods || [];
    return periods.reduce((acc: Record<string, any>, period: any) => {
      if (period?.id) acc[period.id] = period;
      return acc;
    }, {});
  }, [priceSummary?.periods]);
  const priceDocs = priceSummary?.rooms || [];
  const guestCounts = buildGuestCounts(guestParams, filter);

  const buildSearchParams = (
    start?: string | null,
    end?: string | null,
    opts?: { onRequest?: boolean },
  ) =>
    buildSearchParamsHelper({
      start,
      end,
      guestParams,
      filter,
      opts,
    });

  const handleNavigate = (
    start?: string | null,
    end?: string | null,
    opts?: { onRequest?: boolean },
  ) => {
    const params = buildSearchParams(start, end, opts);
    const targetUrl = `/hotel/${hotelId}?${params.toString()}`;
    if (isMobile) {
      navigate(targetUrl);
      return;
    }
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const minStayNotice = buildMinStayNotice(
    priceSummary?.isMinStay,
    priceSummary?.minStay,
  );
  const ctaLabel = buildCtaLabel(
    priceSummary?.onRequestType,
    priceSummary?.hasDirectAvailability,
  );
  const suggestions = React.useMemo(
    () => buildSuggestions(priceSummary),
    [priceSummary],
  );
  const shouldShowSuggestions = shouldShowSuggestionsHelper(priceSummary);
  const shouldShowPriceColumn = shouldShowPriceColumnHelper(priceSummary);
  const roomCountLine = buildRoomCountLine(priceSummary?.availableRoomsCount);
  const dayExtras = (primaryRoom?.dayExtrasDescriptions || []) as string[];
  const hasDayExtras = Array.isArray(dayExtras) && dayExtras.length > 0;
  const renderDayExtras = () =>
    hasDayExtras ? (
      <div className="hotel-item-day-extras">
        {dayExtras.map((text, index) => (
          <div key={`hotel-item-day-extra-${index}`} className="day-extra">
            {text}
          </div>
        ))}
      </div>
    ) : null;

  React.useEffect(() => {
    try {
      const stored = localStorage.getItem("poupas-hotel-favorites");
      if (stored) {
        const parsed: string[] = JSON.parse(stored);
        setIsFavorite(parsed.includes(hotelId));
      }
    } catch (error) {
      console.error("Failed to read favorites", error);
    }
  }, [hotelId]);

  const toggleFavorite = () => {
    try {
      const stored = localStorage.getItem("poupas-hotel-favorites");
      const parsed: string[] = stored ? JSON.parse(stored) : [];
      let updated: string[];
      if (parsed.includes(hotelId)) {
        updated = parsed.filter((id) => id !== hotelId);
        setIsFavorite(false);
        void message.success("Αφαιρέθηκε από τα αγαπημένα");
      } else {
        updated = [...parsed, hotelId];
        setIsFavorite(true);
        void message.success("Προστέθηκε στα αγαπημένα");
      }
      localStorage.setItem("poupas-hotel-favorites", JSON.stringify(updated));
    } catch (error) {
      console.error("Failed to toggle favorite", error);
      void message.error("Κάτι πήγε στραβά με τα αγαπημένα");
    }
  };

  const handleShare = async () => {
    try {
      const params = buildSearchParams(
        priceSummary?.startDate,
        priceSummary?.endDate,
      );
      const shareUrl = `${window.location.origin}/hotel/${hotelId}?${params.toString()}`;
      if (navigator.share) {
        await navigator.share({
          title: hotel.name,
          url: shareUrl,
          text: roomLine || hotel.name,
        });
        void message.success("Το link κοινοποιήθηκε");
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        void message.success("Το link αντιγράφηκε");
      } else {
        void message.info(shareUrl);
      }
    } catch (error) {
      console.error("Share failed", error);
      void message.error("Αδυναμία κοινοποίησης");
    }
  };
  return (
    <HotelItemWrapper
      className={`${priceSummary?.onRequestType ? "on-request" : ""} ${
        shouldShowPriceColumn ? "" : "no-price"
      }`}
    >
      <div className="hotel-item-image">
        <ImageGallery
          images={hotel?.images || images || []}
          showThumbnails={false}
          showPlayButton={false}
        />
      </div>
      <div className="hotel-item-body">
        <div className="hotel-item-header">
          <div className="hotel-item-name-row">
            <div className="hotel-item-name-block">
              <h2 className="hotel-item-name">{hotel.name}</h2>
              <Rate disabled defaultValue={hotel.starRating || 0} />
            </div>
          </div>
          <div className="hotel-item-actions" aria-label="Actions">
            <span
              className="action-icon"
              role="button"
              aria-label="Share"
              onClick={handleShare}
            >
              <ShareAltOutlined />
            </span>
            <span
              className={`action-icon ${isFavorite ? "is-favorite" : ""}`}
              role="button"
              aria-label="Favorite"
              onClick={toggleFavorite}
            >
              {isFavorite ? <HeartFilled /> : <HeartOutlined />}
            </span>
          </div>
        </div>
        <div className="hotel-item-location">
          {[
            formatReadableAddress(hotel?.address),
            formatReadableCity(hotel?.city),
          ]
            .filter((part) => !!part)
            .join(", ")}
        </div>
        {(roomLine || mealIncludeLine || roomCountLine) && (
          <div className="hotel-item-meta">
            {roomLine && <div>{roomLine}</div>}
            {mealIncludeLine && (
              <div className="meta-muted">{mealIncludeLine}</div>
            )}
            {roomCountLine && (
              <div className="meta-highlight">{roomCountLine}</div>
            )}
          </div>
        )}
        {renderDayExtras()}
        {priceSummary?.hasEarlyBookingDiscount && (
          <div className="hotel-item-early-discount">
            Εξοικονόμηση Early Booking Discount
          </div>
        )}
        {priceSummary?.isMinStay && (
          <div className="hotel-item-notice warning">{minStayNotice}</div>
        )}
        {onRequestMessage && (
          <div className="hotel-item-notice info">{onRequestMessage}</div>
        )}
        {suggestions.length > 0 ? (
          <div className="hotel-item-suggestions">
            {suggestions.map((suggestion) => {
              const nightsCount = Math.max(
                suggestion.end.diff(suggestion.start, "days"),
                1,
              );
              const metaParts = [
                `${nightsCount + 1} Ημέρες / ${nightsCount} Νύκτες`,
              ];
              if (priceSummary?.guestsLabel) {
                metaParts.push(priceSummary.guestsLabel);
              }
              const suggestionTotal = calculateTotalForRange({
                start: suggestion.start,
                end: suggestion.end,
                priceDocs,
                periodMap,
                guestCounts,
                normalizedPartnerId,
                hotelId,
              });
              const suggestionPriceLabel = formatPrice(suggestionTotal);
              return (
                <SuggestionBox
                  key={suggestion.key}
                  hotelId={hotelId}
                  startDate={suggestion.start.format("YYYY-MM-DD")}
                  endDate={suggestion.end.format("YYYY-MM-DD")}
                  adults={guestCounts.adults}
                  children={guestCounts.children}
                  infants={guestCounts.infants}
                  partnerId={partnerId}
                  dateRange={`${suggestion.start.format("D MMM YYYY")} - ${suggestion.end.format("D MMM YYYY")}`}
                  meta={metaParts.join(" | ")}
                  priceLabel={suggestionPriceLabel}
                  status={
                    suggestion.status === "on-request"
                      ? "on-request"
                      : "available"
                  }
                  onClick={() =>
                    handleNavigate(
                      suggestion.start.format("YYYY-MM-DD"),
                      suggestion.end.format("YYYY-MM-DD"),
                      suggestion.status === "on-request"
                        ? { onRequest: true }
                        : undefined,
                    )
                  }
                />
              );
            })}
          </div>
        ) : shouldShowSuggestions ? (
          <div className="hotel-item-notice info">
            Δεν υπάρχουν διαθέσιμες προτάσεις.
          </div>
        ) : null}
      </div>
      {shouldShowPriceColumn && (
        <div className="hotel-item-price-column">
          <div className="price-amounts">
            {hasDiscountPrice && discountPriceLabel !== priceLabel && (
              <div className="price-amount selling">{discountPriceLabel}</div>
            )}
            <div className="price-amount">{priceLabel}</div>
          </div>
          <div className="price-meta">
            {nightsLabel}
            {priceSummary?.guestsLabel ? `, ${priceSummary.guestsLabel}` : ""}
          </div>
          <Button
            type="primary"
            className="price-cta"
            onClick={() =>
              handleNavigate(priceSummary?.startDate, priceSummary?.endDate)
            }
          >
            {ctaLabel}
          </Button>
          {showBirthdayPromo && (
            <img
              className="price-promo-image"
              src={hotelBirthdayPromoConfig.imagePath}
              alt="Birthday Offer -9%"
            />
          )}
          {/* {priceSummary?.onRequestType &&
            priceSummary?.hasDirectAvailability && (
              <Button
                className="price-cta secondary on-request-cta"
                onClick={() =>
                  handleNavigate(
                    priceSummary?.startDate,
                    priceSummary?.endDate,
                    {
                      onRequest: true,
                    },
                  )
                }
              >
                On Request
              </Button>
            )} */}
        </div>
      )}
    </HotelItemWrapper>
  );
};
